import { useLocation } from 'js/lib/useRouter';
import { get } from 'js/lib/user';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import { useGetLoggedInUserSubscriptions } from 'bundles/my-purchases-graphql/components/utils/useGetLoggedInUserSubscriptions';
import SubscriptionStatus from 'bundles/subscriptions/common/SubscriptionStatus';
import {
  DISMISSED_SUBSCRIPTION_IDS_STORAGE_KEY,
  useUndoCancellationDataForLihp,
} from 'bundles/subscriptions/utils/cancellationUndoUtils';

// Do not change the value
const CPLUS_WELCOME_NOTIFICATION_STORAGE_KEY_PREFIX = 'coursera_plus_welcome_notification_banner_dismissed';
const CPLUS_ANNUAL_UPGRADE_NOTIFICATION_STORAGE_KEY_PREFIX =
  'lihp_cplus_monthly_to_annual_notification_banner_dismissed';

const getStorageKey = (storageKeyPrefix: string) => {
  const userId = get().id;
  return `${storageKeyPrefix}-${userId}`;
};

/**
 * The priority order for displaying notifications is as follows:
 * 1. **Reactivation Notification** - Highest priority, shown when a subscription is canceled.
 * 2. **Welcome Notification** - Shown for new C+ subscriptions (both monthly and annual).
 * 3. **Upgrade Notification** - Shown when there is an active C+ monthly subscription.
 */

export const useNotificationBannersHandler = () => {
  const { subscriptions } = useGetLoggedInUserSubscriptions({ skip: false });
  const location = useLocation();
  const isCourseraPlusSuccess = location.query?.courseraPlusSuccess;
  const cplusSubscription = subscriptions?.find((subscription) => {
    const { isCourseraPlusSubscription, status } = subscription || {};
    return Boolean(
      (isCourseraPlusSubscription && status === SubscriptionStatus.ACTIVE) || status === SubscriptionStatus.FREE_TRIAL
    );
  });

  const dismissedSubscriptionIds = localStorageEx.getItem(DISMISSED_SUBSCRIPTION_IDS_STORAGE_KEY, JSON.parse, []);

  const { showUndoCancellationCTA, loading, restorationEligibilityEndsAt } = useUndoCancellationDataForLihp({
    dismissedSubscriptionIds,
  });

  const showReactivateSubscriptionNotification = Boolean(showUndoCancellationCTA && restorationEligibilityEndsAt);

  const cplusWelcomeNotificationStorageKey = getStorageKey(CPLUS_WELCOME_NOTIFICATION_STORAGE_KEY_PREFIX);
  const showCourseraPlusWelcomeNotification =
    Boolean(cplusSubscription) &&
    isCourseraPlusSuccess &&
    !localStorageEx.getItem(cplusWelcomeNotificationStorageKey, JSON.parse, false) &&
    !showReactivateSubscriptionNotification;

  const showCplusUpgradeNotificaiton = !showReactivateSubscriptionNotification && !showCourseraPlusWelcomeNotification;

  return {
    loading,
    cplusWelcomeNotificationBannerData: {
      showBanner: showCourseraPlusWelcomeNotification,
      storageKey: cplusWelcomeNotificationStorageKey,
    },
    cplusAnnualUpgradeNotificationBannerData: {
      showBanner: showCplusUpgradeNotificaiton,
      storageKey: getStorageKey(CPLUS_ANNUAL_UPGRADE_NOTIFICATION_STORAGE_KEY_PREFIX),
    },
  };
};
